import { INVOICE_GROUPING_VALUES, INVOICE_CHART_DRILL_DOWN_STATE, TABLE_BREADCRUMB_ITEMS, TABLE_GROUP_TYPES } from '../../../../util/enums/invoice-management';

/**
 * Utility functions for handling breadcrumb navigation in invoice management
 */

/**
 * Finds a stage recursively in the project hierarchy
 * @param {Array} stages - Array of stages to search in
 * @param {string} stageId - ID of the stage to find
 * @returns {Object|null} - Found stage object or null
 */
export const findStageRecursively = (stages, stageId) => {
    for (const stage of stages) {
        if (stage.id === stageId) {
            return stage;
        }
        if (stage.stages && stage.stages.length > 0) {
            const found = findStageRecursively(stage.stages, stageId);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Handles chart drill-down state updates based on breadcrumb type
 * @param {Object} item - Breadcrumb item
 * @param {Object} subTable - Current sub-table state
 * @param {string} selectedGroup - Current selected group
 * @param {Function} setChartDrillDownState - Function to update chart drill-down state
 */
export const handleChartDrillDownUpdate = (item, subTable, selectedGroup, setChartDrillDownState) => {
    if (item.type?.startsWith('group-')) {
        setChartDrillDownState(null); // Back to top-level grouping for chart
    } else if (item.type === INVOICE_GROUPING_VALUES.PO) {
        // If navigating to a PO breadcrumb, the chart should show PO details
        const projectId = item.projectId || subTable?.projectId;
        const stageId = item.stageId || subTable?.stageId;
        const label = item.label || subTable?.label;
        setChartDrillDownState({ 
            type: INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, 
            projectId, 
            stageId, 
            label, 
            originalGroup: selectedGroup 
        });
    } else if (
        [
            INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES,
            INVOICE_GROUPING_VALUES.MONTH_INVOICES,
            INVOICE_GROUPING_VALUES.QUARTER_INVOICES,
            INVOICE_GROUPING_VALUES.INVOICES
        ].includes(item.type)
    ) {
        // If navigating to invoice details, chart should show invoice details
        setChartDrillDownState({ 
            type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, 
            id: item.id, 
            label: item.label, 
            originalGroup: selectedGroup 
        });
    }
};

/**
 * Handles project-related breadcrumb clicks
 * @param {Object} item - Breadcrumb item
 * @param {Array} projects - Array of projects
 * @param {Function} handleProjectSelect - Function to handle project selection
 * @param {Function} setFilterState - Function to update filter state
 * @param {Function} setBreadcrumbItems - Function to update breadcrumb items
 * @param {Function} setSubTable - Function to update sub-table state
 * @param {Object} initialFilterFields - Initial filter fields
 */
export const handleProjectBreadcrumb = (
    item, 
    projects, 
    handleProjectSelect, 
    setFilterState, 
    setBreadcrumbItems, 
    setSubTable, 
    initialFilterFields
) => {
    if (!item.id) {
        setFilterState((prevState) => ({
            ...initialFilterFields,
            supplierId: {
                ...initialFilterFields.supplierId,
                options: prevState.supplierId.options,
            },
        }));
        setBreadcrumbItems([]);
        setSubTable(null);
    } else {
        const project = projects.find((p) => p.id === item.id);
        if (project) handleProjectSelect(project);
    }
};

/**
 * Handles stage-related breadcrumb clicks
 * @param {Object} item - Breadcrumb item
 * @param {Array} projects - Array of projects
 * @param {Function} handleStageSelect - Function to handle stage selection
 */
export const handleStageBreadcrumb = (item, projects, handleStageSelect) => {
    const project = projects.find((p) => p.id === item.projectId);
    if (project) {
        const stage = findStageRecursively(project.stages, item.id);
        if (stage) handleStageSelect(project, stage);
    }
};

/**
 * Handles task-related breadcrumb clicks
 * @param {Object} item - Breadcrumb item
 * @param {Array} projects - Array of projects
 * @param {Function} handleTaskSelect - Function to handle task selection
 */
export const handleTaskBreadcrumb = (item, projects, handleTaskSelect) => {
    const project = projects.find((p) => p.id === item.projectId);
    if (project) {
        const stage = findStageRecursively(project.stages, item.stageId);
        if (stage) {
            const task = stage.tasks?.find((t) => t.id === item.id);
            if (task) handleTaskSelect(project, stage, task);
        }
    }
};

/**
 * Handles group-related breadcrumb clicks
 * @param {Object} item - Breadcrumb item
 * @param {Function} setSelectedGroup - Function to update selected group
 * @param {Function} setSubTable - Function to update sub-table state
 * @param {Function} setFilterState - Function to update filter state
 * @param {Function} setBreadcrumbItems - Function to update breadcrumb items
 * @param {Function} setChartDrillDownState - Function to update chart drill-down state
 * @param {Object} initialFilterFields - Initial filter fields
 */
export const handleGroupBreadcrumb = (
    item, 
    setSelectedGroup, 
    setSubTable, 
    setFilterState, 
    setBreadcrumbItems, 
    setChartDrillDownState, 
    initialFilterFields
) => {
    const groupType = item.type.replace('group-', '');
    setSelectedGroup(groupType);
    setSubTable(null);
    setFilterState((prevState) => ({
        ...initialFilterFields,
        supplierId: {
            ...initialFilterFields.supplierId,
            options: prevState.supplierId.options,
        },
    }));
    setBreadcrumbItems([{ label: item.label, type: item.type, isActive: true }]);
};

/**
 * Handles PO-related breadcrumb clicks
 * @param {Object} item - Breadcrumb item
 * @param {Object} subTable - Current sub-table state
 * @param {string} selectedGroup - Current selected group
 * @param {Function} setSubTable - Function to update sub-table state
 * @param {Function} setBreadcrumbItems - Function to update breadcrumb items
 * @param {Function} setChartDrillDownState - Function to update chart drill-down state
 * @param {Function} setSelectedGroup - Function to update selected group
 * @param {Function} setFilterState - Function to update filter state
 * @param {Object} initialFilterFields - Initial filter fields
 */
export const handlePOBreadcrumb = (
    item, 
    subTable, 
    selectedGroup, 
    setSubTable, 
    setBreadcrumbItems, 
    setChartDrillDownState, 
    setSelectedGroup, 
    setFilterState, 
    initialFilterFields
) => {
    const projectId = item.projectId || subTable?.projectId;
    const stageId = item.stageId || subTable?.stageId;
    const label = item.label || subTable?.label;

    if (!projectId && !stageId) {
        console.warn('Missing projectId or stageId for PO breadcrumb:', { item, subTable });
        return;
    }

    setSubTable({
        type: INVOICE_GROUPING_VALUES.PO,
        id: stageId || projectId,
        label,
        projectId,
        stageId,
    });

    const groupLabel = selectedGroup === INVOICE_GROUPING_VALUES.PROJECT ? TABLE_BREADCRUMB_ITEMS.PROJECT : 
                      selectedGroup === INVOICE_GROUPING_VALUES.SECTION ? TABLE_BREADCRUMB_ITEMS.SECTION : 
                      TABLE_BREADCRUMB_ITEMS.PO;
    const groupType = selectedGroup === INVOICE_GROUPING_VALUES.PROJECT ? TABLE_GROUP_TYPES.PROJECT : 
                     selectedGroup === INVOICE_GROUPING_VALUES.SECTION ? TABLE_GROUP_TYPES.SECTION : 
                     TABLE_GROUP_TYPES.PO;

    setBreadcrumbItems([
        {
            label: groupLabel,
            type: groupType,
            isActive: false,
            onClick: () => {
                setSubTable(null);
                setSelectedGroup(selectedGroup);
                setFilterState((prevState) => ({
                    ...initialFilterFields,
                    supplierId: {
                        ...initialFilterFields.supplierId,
                        options: prevState.supplierId.options,
                    },
                }));
                setBreadcrumbItems([{ label: groupLabel, type: groupType, isActive: true }]);
                setChartDrillDownState(null);
            },
        },
        {
            label: 'PurchaseOrders',
            type: INVOICE_GROUPING_VALUES.PO,
            id: stageId || projectId,
            isActive: true,
            projectId,
            stageId,
        },
    ]);
};

/**
 * Handles invoice-related breadcrumb clicks
 * @param {Object} item - Breadcrumb item
 * @param {Array} projects - Array of projects
 * @param {string} selectedGroup - Current selected group
 * @param {Function} setSubTable - Function to update sub-table state
 * @param {Function} setBreadcrumbItems - Function to update breadcrumb items
 * @param {Function} setSelectedGroup - Function to update selected group
 * @param {Function} setFilterState - Function to update filter state
 * @param {Function} setChartDrillDownState - Function to update chart drill-down state
 * @param {Function} handlePOClick - Function to handle PO clicks
 * @param {Object} initialFilterFields - Initial filter fields
 */
export const handleInvoiceBreadcrumb = (
    item, 
    projects, 
    selectedGroup, 
    setSubTable, 
    setBreadcrumbItems, 
    setSelectedGroup, 
    setFilterState, 
    setChartDrillDownState, 
    handlePOClick, 
    initialFilterFields
) => {
    setSubTable(item);
    const groupLabel = item.type === INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES ? TABLE_BREADCRUMB_ITEMS.SUPPLIER : 
                      item.type === INVOICE_GROUPING_VALUES.MONTH_INVOICES ? TABLE_BREADCRUMB_ITEMS.MONTH : 
                      item.type === INVOICE_GROUPING_VALUES.QUARTER_INVOICES ? TABLE_BREADCRUMB_ITEMS.QUARTER : 
                      TABLE_BREADCRUMB_ITEMS.PO;
    const groupType = item.type === INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES ? TABLE_GROUP_TYPES.SUPPLIER : 
                     item.type === INVOICE_GROUPING_VALUES.MONTH_INVOICES ? TABLE_GROUP_TYPES.MONTH : 
                     item.type === INVOICE_GROUPING_VALUES.QUARTER_INVOICES ? TABLE_GROUP_TYPES.QUARTER : 
                     TABLE_GROUP_TYPES.PO;

    let breadcrumbBase = [{
        label: groupLabel,
        type: groupType,
        isActive: false,
        onClick: () => {
            setSubTable(null);
            setSelectedGroup(groupType.replace('group-', ''));
            setFilterState((prevState) => ({
                ...initialFilterFields,
                supplierId: {
                    ...initialFilterFields.supplierId,
                    options: prevState.supplierId.options,
                },
            }));
            setBreadcrumbItems([{ label: groupLabel, type: groupType, isActive: true }]);
            setChartDrillDownState(null);
        },
    }];

    if (item.type === INVOICE_GROUPING_VALUES.INVOICES) {
        const poId = item.id;
        const originalGroup = item.originalGroup || selectedGroup;

        // Reconstruct the parent PO breadcrumb based on originalGroup and context
        let poParentLabel = 'PurchaseOrders';
        let poParentProjectId = item.projectId;
        let poParentStageId = item.stageId;
        let poParentIdForBreadcrumb = poParentProjectId;

        if (originalGroup === INVOICE_GROUPING_VALUES.SECTION && poParentStageId) {
            poParentLabel = projects.flatMap(p => p.stages).find(s => s.id === poParentStageId)?.name || 'Section POs';
            poParentIdForBreadcrumb = poParentStageId;
        } else if (originalGroup === INVOICE_GROUPING_VALUES.PROJECT && poParentProjectId) {
            poParentLabel = projects.find(p => p.id === poParentProjectId)?.name || 'Project POs';
            poParentIdForBreadcrumb = poParentProjectId;
        } else if (originalGroup === INVOICE_GROUPING_VALUES.PO) {
            poParentLabel = 'POs';
            poParentIdForBreadcrumb = poId;
        }

        breadcrumbBase.push({
            label: `${poParentLabel}`,
            type: INVOICE_GROUPING_VALUES.PO,
            id: poParentIdForBreadcrumb,
            isActive: false,
            projectId: poParentProjectId,
            stageId: poParentStageId,
            onClick: () => handlePOClick(poParentIdForBreadcrumb, poParentProjectId, poParentStageId),
        });
    }

    setBreadcrumbItems([
        ...breadcrumbBase,
        {
            label: item.label,
            type: item.type,
            id: item.id,
            isActive: true,
        },
    ]);
};

/**
 * Main breadcrumb click handler that routes to appropriate handlers
 * @param {Object} item - Breadcrumb item
 * @param {Object} context - Context object containing all necessary functions and state
 */
export const handleBreadcrumbClick = (item, context) => {
    const {
        projects,
        subTable,
        selectedGroup,
        handleProjectSelect,
        handleStageSelect,
        handleTaskSelect,
        handlePOClick,
        setFilterState,
        setBreadcrumbItems,
        setSubTable: setSubTableFn,
        setSelectedGroup,
        setChartDrillDownState,
        setIsHovering,
        initialFilterFields
    } = context;

    // Handle chart drill-down state updates
    handleChartDrillDownUpdate(item, subTable, selectedGroup, setChartDrillDownState);

    // Route to appropriate handler based on item type
    if (item.type === INVOICE_GROUPING_VALUES.PROJECT) {
        handleProjectBreadcrumb(item, projects, handleProjectSelect, setFilterState, setBreadcrumbItems, setSubTableFn, initialFilterFields);
    } else if (item.type === INVOICE_GROUPING_VALUES.SECTION) {
        handleStageBreadcrumb(item, projects, handleStageSelect);
    } else if (item.type === INVOICE_GROUPING_VALUES.TASK) {
        handleTaskBreadcrumb(item, projects, handleTaskSelect);
    } else if (item.type?.startsWith('group-')) {
        handleGroupBreadcrumb(item, setSelectedGroup, setSubTableFn, setFilterState, setBreadcrumbItems, setChartDrillDownState, initialFilterFields);
    } else if (item.type === INVOICE_GROUPING_VALUES.PO) {
        handlePOBreadcrumb(item, subTable, selectedGroup, setSubTableFn, setBreadcrumbItems, setChartDrillDownState, setSelectedGroup, setFilterState, initialFilterFields);
    } else if (
        [
            INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES,
            INVOICE_GROUPING_VALUES.MONTH_INVOICES,
            INVOICE_GROUPING_VALUES.QUARTER_INVOICES,
            INVOICE_GROUPING_VALUES.INVOICES
        ].includes(item.type)
    ) {
        handleInvoiceBreadcrumb(item, projects, selectedGroup, setSubTableFn, setBreadcrumbItems, setSelectedGroup, setFilterState, setChartDrillDownState, handlePOClick, initialFilterFields);
    }

    setIsHovering(false);
}; 